import { useEffect, useState } from "react";

/**
 * Tracks the mobile-visual-viewport rect (window.visualViewport) so
 * fixed-position modals / floating panels can pin themselves exactly to
 * the visible area when the on-screen keyboard opens on iOS / Android.
 *
 * Returns { height, width, offsetTop, offsetLeft }.
 * Fallback: window.innerHeight/innerWidth when visualViewport is missing.
 */
export default function useVisualViewport() {
  const [rect, setRect] = useState(() => {
    if (typeof window === "undefined")
      return { height: 0, width: 0, offsetTop: 0, offsetLeft: 0 };
    const vv = window.visualViewport;
    return {
      height: vv?.height || window.innerHeight,
      width: vv?.width || window.innerWidth,
      offsetTop: vv?.offsetTop || 0,
      offsetLeft: vv?.offsetLeft || 0,
    };
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const vv = window.visualViewport;
    if (!vv) {
      const onResize = () =>
        setRect({
          height: window.innerHeight,
          width: window.innerWidth,
          offsetTop: 0,
          offsetLeft: 0,
        });
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }
    const onChange = () =>
      setRect({
        height: vv.height,
        width: vv.width,
        offsetTop: vv.offsetTop,
        offsetLeft: vv.offsetLeft,
      });
    vv.addEventListener("resize", onChange);
    vv.addEventListener("scroll", onChange);
    return () => {
      vv.removeEventListener("resize", onChange);
      vv.removeEventListener("scroll", onChange);
    };
  }, []);

  return rect;
}
